/**
 * Client store for per-pane mobile prefs.
 *
 * Server: GET/POST /api/users/mobile-prefs/ (portal UserMobilePrefs).
 * Soft-fails when the endpoint is missing (404) so sandbox can ship the shell
 * before the portal migration is deployed.
 *
 * Contract:
 * - setPaneParams deep-merges partials in memory, then POSTs the full pane object.
 * - Server jsonb_set replaces the whole panes.<paneId> value.
 * - Persist only after hydrate status === 'ready'.
 */

import { useSyncExternalStore } from 'react';

import { apiClient, getCsrfToken } from '../services/api';
import { API_BASE_URL as API_BASE } from '../config/apiBase';

const PREFS_PATH = '/api/users/mobile-prefs/';
const DEBOUNCE_MS = 800;
const PANE_ID_RE = /^[A-Za-z][A-Za-z0-9_]{0,63}$/;

export type PrefsHydrateStatus = 'loading' | 'ready' | 'error';

type PaneParams = Record<string, unknown>;

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

function deepMerge(
  base: PaneParams,
  partial: PaneParams,
): PaneParams {
  const out: PaneParams = { ...base };
  for (const [k, v] of Object.entries(partial)) {
    if (isPlainObject(v) && isPlainObject(out[k])) {
      out[k] = deepMerge(out[k] as PaneParams, v);
    } else {
      out[k] = v;
    }
  }
  return out;
}

type Listener = () => void;

class MobilePrefsStore {
  private panes: Record<string, PaneParams> = {};
  private status: PrefsHydrateStatus = 'loading';
  private listeners = new Set<Listener>();
  private dirty = new Set<string>();
  private timer: ReturnType<typeof setTimeout> | null = null;
  private hydrated = false;

  subscribe = (cb: Listener): (() => void) => {
    this.listeners.add(cb);
    return () => {
      this.listeners.delete(cb);
    };
  };

  private notify(): void {
    for (const fn of this.listeners) fn();
  }

  getStatus(): PrefsHydrateStatus {
    return this.status;
  }

  getPaneParams(paneId: string): PaneParams | undefined {
    if (!this.hydrated) return undefined;
    return this.panes[paneId];
  }

  async hydrate(): Promise<void> {
    this.status = 'loading';
    this.notify();
    try {
      const data = await apiClient.get<{
        panes?: Record<string, PaneParams>;
      }>(PREFS_PATH);
      this.panes = isPlainObject(data?.panes)
        ? (data.panes as Record<string, PaneParams>)
        : {};
      this.status = 'ready';
      this.hydrated = true;
    } catch (err: unknown) {
      const status =
        err &&
        typeof err === 'object' &&
        'response' in err &&
        (err as { response?: { status?: number } }).response?.status;
      // Soft-fail missing endpoint or empty row as ready-empty so panels mount.
      if (status === 404) {
        this.panes = {};
        this.status = 'ready';
        this.hydrated = true;
      } else {
        this.status = 'error';
        this.hydrated = false;
      }
    }
    this.notify();
  }

  setPaneParams(paneId: string, partial: PaneParams): void {
    if (this.status !== 'ready') return;
    if (!PANE_ID_RE.test(paneId)) return;
    const prev = this.panes[paneId] ?? {};
    this.panes[paneId] = deepMerge(prev, partial);
    this.dirty.add(paneId);
    this.notify();
    this.scheduleFlush();
  }

  private scheduleFlush(): void {
    if (this.timer) clearTimeout(this.timer);
    this.timer = setTimeout(() => {
      this.timer = null;
      void this.flush();
    }, DEBOUNCE_MS);
  }

  async flush(): Promise<void> {
    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = null;
    }
    if (this.status !== 'ready' || this.dirty.size === 0) return;

    const ids = [...this.dirty];
    this.dirty.clear();

    for (const paneId of ids) {
      const params = this.panes[paneId] ?? {};
      try {
        await this.postPane(paneId, params, false);
      } catch {
        this.dirty.add(paneId);
      }
    }
  }

  /**
   * Immediate keepalive POST for pagehide / visibility hidden.
   * Must not use axios 401→login redirect path.
   */
  flushKeepalive(): void {
    if (this.status !== 'ready' || this.dirty.size === 0) return;
    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = null;
    }
    const ids = [...this.dirty];
    this.dirty.clear();
    for (const paneId of ids) {
      const params = this.panes[paneId] ?? {};
      void this.postPane(paneId, params, true);
    }
  }

  private async postPane(
    paneId: string,
    params: PaneParams,
    keepalive: boolean,
  ): Promise<void> {
    const body = JSON.stringify({ paneId, params });
    if (keepalive) {
      const url = `${API_BASE.replace(/\/$/, '')}${PREFS_PATH}`;
      const res = await fetch(url, {
        method: 'POST',
        credentials: 'include',
        keepalive: true,
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': getCsrfToken() || '',
        },
        body,
      });
      if (!res.ok && res.status !== 404) {
        // Re-queue for next chance; do not redirect on unload.
        this.dirty.add(paneId);
      }
      return;
    }
    await apiClient.post(PREFS_PATH, { paneId, params });
  }
}

export const mobilePrefsStore = new MobilePrefsStore();

/** Bind pagehide flush once (call from MobileWorkspace mount). */
export function bindMobilePrefsLifecycle(): () => void {
  const onHide = () => {
    if (document.visibilityState === 'hidden') {
      mobilePrefsStore.flushKeepalive();
    }
  };
  const onPageHide = () => {
    mobilePrefsStore.flushKeepalive();
  };
  document.addEventListener('visibilitychange', onHide);
  window.addEventListener('pagehide', onPageHide);
  return () => {
    document.removeEventListener('visibilitychange', onHide);
    window.removeEventListener('pagehide', onPageHide);
    void mobilePrefsStore.flush();
  };
}

/**
 * Live params for one pane. Returns `undefined` until hydrate resolves —
 * callers must NOT treat that as "empty stored state".
 */
export function usePaneParams(paneId: string): unknown {
  return useSyncExternalStore(mobilePrefsStore.subscribe, () =>
    mobilePrefsStore.getPaneParams(paneId),
  );
}

/**
 * Settled, not successful. A non-404 hydrate failure leaves status 'error'
 * with hydrated=false; gating on 'ready' would strand that user on a
 * permanent loading screen. Writes stay disabled via setPaneParams' own
 * `status !== 'ready'` guard.
 */
export function useMobilePrefsSettled(): boolean {
  return useSyncExternalStore(
    mobilePrefsStore.subscribe,
    () => mobilePrefsStore.getStatus() !== 'loading',
  );
}
